<template>
  <div class="flex flex-col gap-5">
    <div v-if="!isManualEntry">
      <b-combobox
        :label="t('modal_create_mission_step1.company_name')"
        :placeholder="t('modal_create_mission_step1.select')"
        v-model="selectedCompany"
        :items="companiesItems"
        :min-search-length="2"
        :loading="isSearchingCompany"
        @search="onCompanySearch"
        data-e2e="experience-company-combobox"
      />
    </div>
    <div v-else class="flex flex-col gap-4">
      <b-text-field
        v-model="manualCompanyName"
        label="Nom de l'entreprise"
        placeholder="Nom de l'entreprise"
        data-e2e="experience-manual-company-name-input"
      />
      <b-text-field
        v-model="manualCompanySiren"
        label="SIREN de l'entreprise"
        placeholder="SIREN"
        min="9"
        data-e2e="experience-manual-siren-input"
      />
    </div>

<!--    <b-text-field-->
<!--      v-model="contact_email"-->
<!--      :label="t('modal_create_mission_step1.client.email')"-->
<!--    />-->

    <div
      v-if="isEditMode && isLogoEligibleCompany"
      class="border border-[#373737] rounded-xl overflow-hidden px-4 py-3 flex flex-col gap-3"
      data-e2e="experience-logo-request-block"
    >
      <div class="flex items-center gap-3">
        <img v-if="existingLogoUrl" :src="existingLogoUrl" alt="" class="size-12 rounded-full object-cover" />
        <span class="text-white text-sm font-medium">{{ t('modal_create_experience.company_logo') }}</span>
      </div>

      <p
        v-if="logoRequestPending"
        class="text-sm text-text-secondary"
        data-e2e="experience-logo-request-pending"
      >
        {{ t('modal_create_experience.logo_request_pending') }}
      </p>
      <template v-else>
        <b-button
          v-if="!showLogoUpload"
          size="xs"
          light
          data-e2e="experience-propose-logo-button"
          @click="showLogoUpload = true"
        >
          {{ t('modal_create_experience.propose_logo_cta') }}
        </b-button>
        <b-file-upload
          v-else
          :loading="false"
          :title="t('modal_create_experience.company_logo')"
          :subtitle="t('modal_create_experience.company_logo_hint')"
          :accepted-types="['image/png', 'image/jpeg', 'image/jpg', 'image/webp']"
          :max-size-mb="5"
          @change="onLogoRequestChange"
        />
      </template>
    </div>

    <RadioGroup v-model="selectedCompanySize" data-e2e="experience-company-size-group">
      <b-label class="text-white mb-1 block">{{ t('modal_create_mission_step1.company_type' )}}</b-label>
      <div class="flex flex-wrap gap-2">
        <RadioGroupOption v-slot="{ checked }" :value="companySize.value" v-for="(companySize, index) in companySizesItems" :key="companySize.value">
          <BRadioChip :checked="checked" :data-e2e="`experience-company-size-option-${index}`">
            {{ companySize.label }}
          </BRadioChip>
        </RadioGroupOption>
      </div>
    </RadioGroup>

    <b-select searchable :items="industriesItems" top v-model="selectedIndustry" :label="t('modal_create_mission_step1.industry_sector')" data-e2e="experience-industry-select" />

    <RadioGroup v-model="selectedContractType" data-e2e="experience-contract-type-group">
      <b-label class="text-white mb-1 block">{{ t('modal_create_experience.contract_type' )}}</b-label>
      <div class="flex flex-wrap gap-2">
          <RadioGroupOption v-slot="{ checked }" :value="type.value" v-for="(type, index) in contractTypes" :key="type.value">
            <BRadioChip :checked="checked" :data-e2e="`experience-contract-type-option-${index}`">{{ type.label }}</BRadioChip>
          </RadioGroupOption>
      </div>
    </RadioGroup>

    <div class="flex flex-col gap-2">
      <div class="grid grid-cols-2 gap-4">
        <div data-e2e="experience-start-date">
          <b-date-picker
            v-model="startDate"
            granularity="month"
            :label="t('modal_create_experience.start_date')"
            :placeholder="t('modal_create_experience.start_date')"
          />
        </div>
        <div v-if="!isCurrentPosition" data-e2e="experience-end-date">
          <b-date-picker
            v-model="endDate"
            granularity="month"
            :label="t('modal_create_experience.end_date')"
            :placeholder="t('modal_create_experience.end_date')"
          />
        </div>
      </div>
      <small
        v-if="endBeforeStart"
        class="text-red-500"
        data-e2e="experience-date-error"
      >
        {{ t('modal_create_experience.end_date_before_start') }}
      </small>
      <b-checkbox
        v-model="isCurrentPosition"
        :label="t('modal_create_experience.current_position')"
        data-e2e="experience-current-position-toggle"
      />
    </div>

  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, toRefs } from 'vue';
import BSelect from "~/components/forms/select/BSelect.vue";
import BTextField from "~/components/forms/BTextField.vue";
import BCombobox, { type ComboboxItem } from "~/components/forms/BCombobox.vue";
import {useCompanyStore} from "~/stores/companyStore";
import { groupByCategory, toSelectOptions } from "~/utils/categoryGrouping";
import { RadioGroup, RadioGroupOption } from "@headlessui/vue";
import BRadioChip from "~/components/forms/radio/BRadioChip.vue";
import BLabel from "~/components/forms/BLabel.vue";
import BCheckbox from "~/components/forms/checkbox/BCheckbox.vue";
import BFileUpload from "~/components/forms/BFileUpload.vue";
import BButton from "~/components/forms/BButton.vue";
import BDatePicker from "~/components/forms/BDatePicker.vue";
import type { Experience } from "~/api/v2/experiences/ExperienceRepository";

const { t } = useI18n();

interface Props {
  experience?: Experience;
  initialData?: Record<string, any>;
  logoRequestPending?: boolean;
}

const props = defineProps<Props>()
const { experience, initialData, logoRequestPending } = toRefs(props);

const emit = defineEmits(['change', 'logo-request'])
const companySizeStore = useCompanySizeStore()
const industryStore = useIndustryStore()
const companyStore = useCompanyStore()

const { companySizes } = storeToRefs(companySizeStore)
const { industries } = storeToRefs(industryStore)

const contractTypes = ref([
  { label: 'Freelance', value: 'freelance' },
  { label: 'CDI/CDD', value: 'permanent' }
])

const formatDateToYYYYMMDD = (dateToFormat) => {
  if (!dateToFormat) return '';
  // Handles both Date objects and string dates
  const d = new Date(dateToFormat);
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const selectedCompanySize = ref()
const selectedContractType = ref()
const selectedIndustry = ref<{ value: string; label: string } | null>(null)
const selectedCompany = ref<ComboboxItem | null>(null)
const contact_email = ref('')
const date = ref({})

const ymToDate = (ym?: string): Date | null =>
  ym ? new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1)) : null;

const dateToYM = (d: Date | null): string =>
  d ? `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}` : '';

const startDate = computed<Date | null>({
  get: () => ymToDate(date.value.start),
  set: (v) => { date.value.start = dateToYM(v); },
});

const endDate = computed<Date | null>({
  get: () => ymToDate(date.value.end),
  set: (v) => { date.value.end = dateToYM(v); },
});

const isManualEntry = ref(false)
const manualCompanyName = ref('')
const manualCompanySiren = ref('')
const currentSearchQuery = ref('')
const isSearchingCompany = ref(false)
const isCurrentPosition = ref(false)
const previousEndDate = ref('')
const showLogoUpload = ref(false)

// A logo correction can only be proposed in edit mode (an experience already
// exists), and only for a company that hasn't opted out of logo edits
// (`is_logo_editable === false`, e.g. it already has its own users managing it).
const isEditMode = computed(() => !!experience.value)
const existingLogoUrl = computed(() => (experience.value as any)?.company?.logo_url ?? '')
const isLogoEligibleCompany = computed(() => (experience.value as any)?.company?.is_logo_editable !== false)

// Dates are "YYYY-MM" strings, so a plain string comparison is chronological.
// Flags an incoherent range (end before start) when both dates are set and the
// experience is not marked as the current position.
const endBeforeStart = computed(() => {
  if (isCurrentPosition.value) return false
  const { start, end } = date.value
  return !!(start && end && end < start)
})

const onLogoRequestChange = (file: File) => {
  showLogoUpload.value = false
  emit('logo-request', file)
}

// Flag to prevent emitting during draft restoration
const isRestoringData = ref(false)

const companySizesItems = computed(() => toSelectOptions(companySizes.value || [], t('modal_create_experience.people_label')))
const industriesItems = computed(() => groupByCategory(industries.value || []))
const companiesItems = ref<ComboboxItem[]|[]>([])

watch(experience, (experience) => {
  if (experience) {
    const formatDateToYYYYMM = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      return `${year}-${month}`;
    };

    // Handle company data (may be null for LinkedIn-imported experiences)
    if (experience.company) {
      selectedCompany.value = { value: experience.company.siren || '', label: experience.company.public_name || experience.company.business_name || '' }
    }

    date.value.start = formatDateToYYYYMM(experience.start_at)
    date.value.end = formatDateToYYYYMM(experience.end_at)

    // Handle industry (may be null for LinkedIn-imported experiences)
    if (experience.industry) {
      selectedIndustry.value = { value: experience.industry.industry_id, label: experience.industry.name }
    }

    // Handle company_size (may be null for LinkedIn-imported experiences)
    if (experience.company_size) {
      selectedCompanySize.value = experience.company_size.company_size_id
    }

    selectedContractType.value = experience.contract_type === 'freelance' ? 'freelance' : 'permanent'

    isCurrentPosition.value = !experience.end_at
  }
}, { immediate: true, deep: true })

watch(isCurrentPosition, (checked) => {
  if (checked) {
    previousEndDate.value = date.value.end || ''
    date.value.end = null
  } else {
    date.value.end = previousEndDate.value
  }
})

// Watch for draft restoration
watch(initialData, (data) => {
  if (data && Object.keys(data).length > 0) {
    isRestoringData.value = true
    const formatDateToYYYYMM = (dateString) => {
      if (!dateString) return '';
      // Handle YYYY-MM-DD format from draft
      return dateString.substring(0, 7);
    };

    if (data.company_identifier) {
      if (data.company_name && data.company_identifier === data.company_name) {
        // Manual entry mode
        isManualEntry.value = true
        manualCompanyName.value = data.company_name || ''
        manualCompanySiren.value = data.company_identifier || ''
      } else {
        // Company selected from search - use company_label for display
        selectedCompany.value = { value: data.company_identifier, label: data.company_label || data.company_name || data.company_identifier }
      }
    }
    if (data.start_at) {
      date.value.start = formatDateToYYYYMM(data.start_at)
    }
    if (data.end_at) {
      date.value.end = formatDateToYYYYMM(data.end_at)
    }
    if (data.is_current_position !== undefined) {
      isCurrentPosition.value = data.is_current_position
    }
    if (data.company_size_id) {
      selectedCompanySize.value = data.company_size_id
    }
    if (data.contract_type) {
      selectedContractType.value = data.contract_type
    }
    if (data.industry_id) {
      selectedIndustry.value = { value: data.industry_id, label: data.industry_label || data.industry_id }
    }
    // Reset flag after Vue processes all updates
    nextTick(() => {
      isRestoringData.value = false
    })
  }
}, { immediate: true, deep: true })

const onCompanySearch = (query: string) => {
  currentSearchQuery.value = query;
  isSearchingCompany.value = true;
  // Always expose the manual entry option synchronously so the flow never
  // depends on the external gouv/SIRENE API responding (or even settling) in time.
  const manualOption = { value: 'manual', label: 'Ajouter une entreprise manuellement' };
  companiesItems.value = [manualOption];
  companyStore.searchCompanies(query, 'external').then((response) => {
    if ('companies' in response && response.companies.length > 0) {
      const results = response.companies.map((company) => {
        const sublabel = [company.postal_code, company.city].filter(Boolean).join(' ')
        return {
          value: company.siren,
          label: company.business_name,
          sublabel: sublabel || undefined
        }
      })
      companiesItems.value = [...results, manualOption]
    }
    // No results / error: keep the manual-only list already set above.
  }).catch(() => {
    // Network failure: keep the manual-only list already set above.
  }).finally(() => {
    isSearchingCompany.value = false;
  })
}

watch(selectedCompany, (value) => {
  if (value?.value === 'manual') {
    isManualEntry.value = true;
    manualCompanyName.value = currentSearchQuery.value;
  }
});

onMounted(() => {
  companySizeStore.fetchCompanySizes()
  industryStore.fetchIndustries()
})

watch([
  selectedCompanySize,
  selectedIndustry,
  selectedCompany,
  date,
  contact_email,
  manualCompanyName,
  manualCompanySiren,
  selectedContractType,
  isCurrentPosition,
], () => {
  // Skip emitting during draft restoration to prevent infinite loops
  if (isRestoringData.value) return

  const payload: any = {
    company_size_id: selectedCompanySize.value,
    industry_id: selectedIndustry.value?.value,
    industry_label: selectedIndustry.value?.label,
    contact_email: contact_email.value,
    start_at: date.value.start ? `${date.value.start}-01` : undefined,
    end_at: isCurrentPosition.value ? null : (date.value.end ? `${date.value.end}-01` : undefined),
    contract_type: selectedContractType.value,
    is_current_position: isCurrentPosition.value,
  };

  if (isManualEntry.value) {
    payload.company_name = manualCompanyName.value;
    payload.company_identifier = manualCompanySiren.value;
  } else {
    payload.company_identifier = selectedCompany.value?.value;
    payload.company_label = selectedCompany.value?.label;
  }

  emit('change', payload)
}, { deep: true })
</script>
